import useVoiceInput from '../../../hooks/useVoiceInput';
import React, { useState, useRef, useEffect } from 'react';
import { FiTrash2, FiCalendar, FiUser, FiCheckCircle, FiCircle, FiPlus, FiX, FiChevronRight, FiChevronDown, FiList, FiZap, FiMic } from 'react-icons/fi';
import AIPopover from './AIPopover';
import ListeningOverlay from './ListeningOverlay';
import DeleteConfirmationModal from './DeleteConfirmationModal';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { updateSubtask, deleteSubtask, addSubtask, countSubtasks } from '../services/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { useTask } from '../contexts/TaskContext';
import toast from 'react-hot-toast';
import HxSelect from '../../../components/ui/HxSelect';
import { UserRound as LUser } from 'lucide-react';
import DatePickerPopover from './DatePickerPopover';
import { format, isToday, isTomorrow } from 'date-fns';
import { es } from 'date-fns/locale';

// Inline editable text for subtask titles (click text = edit, click row background = open subtask)
const SubtaskInlineEdit = ({ value, onSave, isCompleted }) => {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef(null);

    useEffect(() => { setEditValue(value); }, [value]);
    useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

    const handleBlur = () => {
        setEditing(false);
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== value) onSave(trimmed);
        else setEditValue(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
        if (e.key === 'Escape') { setEditValue(value); setEditing(false); }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 text-[12px] bg-white border border-blue-400 rounded px-1 py-0 outline-none ring-1 ring-blue-100 text-gray-900"
            />
        );
    }

    return (
        <span
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className={`cursor-text hover:bg-gray-100 rounded px-1 py-0 truncate text-[12px] ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-700'}`}
            title="Clic para editar"
        >
            {value}
        </span>
    );
};

const SubtaskRow = ({ subtask, task, level = 0, parentPath = [], onOpenSubtask }) => {
    // ... existing state ...
    const { user } = useAuth();
    const { selectedProjectId, selectedFolderId, selectedListId, users, setSelectedTaskId, setSelectedSubtaskPath, setShowTaskDrawer } = useTask();

    // Use the task's own location IDs for Firestore operations (critical for cross-project "Mis Tareas")
    const taskProjectId = task?.projectId || selectedProjectId;
    const taskFolderId = task?.folderId || selectedFolderId;
    const taskListId = task?.listId || selectedListId;
    // State
    const [isExpanded, setIsExpanded] = useState(true);
    const [isAddingChild, setIsAddingChild] = useState(false);
    const [newChildTitle, setNewChildTitle] = useState('');
    const [newChildDate, setNewChildDate] = useState('');
    const [newChildAssignee, setNewChildAssignee] = useState('');
    const [showChildDatePicker, setShowChildDatePicker] = useState(false);
    const [childAddAiPopover, setChildAddAiPopover] = useState(null);
    const [aiPopover, setAiPopover] = useState(null);
    const [assigningSubtaskId, setAssigningSubtaskId] = useState(null);
    const [datePickerSubtaskId, setDatePickerSubtaskId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const currentPath = [...parentPath, subtask.id];
    const subtaskCount = countSubtasks(subtask.subtareas || []);
    const hasSubtasks = subtaskCount > 0;

    // Voice Dictation for Child Subtasks
    const { isRecording: isRecordingChild, interimTranscript: interimChild, startRecording: startRecordingChild, stopRecording: stopRecordingChild } = useVoiceInput((text) => {
        setNewChildTitle(prev => prev ? `${prev} ${text}` : text);
    });

    const handleOpenSubtask = () => {
        setSelectedTaskId(task.id);
        setSelectedSubtaskPath(currentPath);
        setShowTaskDrawer(true);
    };

    const handleUpdate = async (updates) => {
        try {
            await updateSubtask(user.uid, taskProjectId, taskFolderId, taskListId, task.id, currentPath, updates);
            toast.success('Subtarea actualizada');
        } catch (error) {
            console.error(error);
            toast.error('Error al actualizar');
        }
    };

    const handleDelete = async () => {
        try {
            await deleteSubtask(user.uid, taskProjectId, taskFolderId, taskListId, task.id, currentPath);
            toast.success('Subtarea eliminada');
        } catch (error) {
            console.error(error);
            toast.error('Error al eliminar');
        }
    };

    const handleAssign = async (email) => {
        try {
            await updateSubtask(user.uid, taskProjectId, taskFolderId, taskListId, task.id, currentPath, {
                asignados: email ? [email] : []
            });
            setAssigningSubtaskId(null);
            toast.success('Asignado actualizado');
        } catch (error) {
            console.error(error);
            toast.error('Error al asignar');
        }
    };

    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isToday(date)) return `Hoy, ${format(date, 'HH:mm')}`;
        if (isTomorrow(date)) return `Mañana, ${format(date, 'HH:mm')}`;
        return format(date, 'd MMM, HH:mm', { locale: es });
    };

    const assignedEmail = subtask.asignados?.[0];
    const assignedUser = users.find(u => u.email === assignedEmail);

    let dateStr = null;
    if (subtask.fechaVencimiento) {
        dateStr = typeof subtask.fechaVencimiento === 'object' && subtask.fechaVencimiento.seconds
            ? new Date(subtask.fechaVencimiento.seconds * 1000).toISOString()
            : subtask.fechaVencimiento;
    }

    const indentStyle = { paddingLeft: `${level * 24}px` };

    // ... existing handlers (handleOpenSubtask, handleUpdate, handleDelete) ...

    const handleAddChild = async () => {
        if (!newChildTitle.trim()) return;
        try {
            const childData = {
                titulo: newChildTitle,
                estado: 'planificacion'
            };
            if (newChildDate) childData.fechaVencimiento = newChildDate;
            if (newChildAssignee) childData.asignados = [newChildAssignee];
            await addSubtask(user.uid, taskProjectId, taskFolderId, taskListId, task.id, childData, currentPath);
            setNewChildTitle('');
            setNewChildDate('');
            setNewChildAssignee('');
            setIsAddingChild(false);
            setIsExpanded(true);
            toast.success('Subtarea creada');
        } catch (error) {
            console.error(error);
            toast.error('Error al crear subtarea');
        }
    };

    const resetChildForm = () => {
        setIsAddingChild(false);
        setNewChildTitle('');
        setNewChildDate('');
        setNewChildAssignee('');
        setShowChildDatePicker(false);
        setChildAddAiPopover(null);
    };

    // ... rest of the component until Render ...

    return (
        <>
            {/* ... Existing Row Rendering ... */}
            <div onClick={handleOpenSubtask} className="flex items-center gap-1 px-1.5 py-[3px] hover:bg-gray-50/80 rounded group transition-colors border-b border-gray-100/50 last:border-b-0 relative cursor-pointer">
                {/* Name & Checkbox */}
                <div className="flex items-center gap-1 flex-1 min-w-0" style={indentStyle}>
                    {hasSubtasks ? (
                        <button onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                            {isExpanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
                        </button>
                    ) : (
                        <div className="w-3.5" />
                    )}

                    <button
                        onClick={(e) => { e.stopPropagation(); handleUpdate({ completado: !subtask.completado }); }}
                        className={`flex-shrink-0 ${subtask.completado ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        {subtask.completado ? <FiCheckCircle size={15} /> : <FiCircle size={15} />}
                    </button>

                    <SubtaskInlineEdit
                        value={subtask.titulo}
                        isCompleted={subtask.completado}
                        onSave={(newTitle) => handleUpdate({ titulo: newTitle })}
                    />

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setAiPopover({ initialContent: subtask.titulo, position: { top: rect.bottom + 5, left: rect.left } });
                        }}
                        className="opacity-0 group-hover:opacity-100 text-indigo-400 hover:text-indigo-600 p-0.5 rounded-full transition-opacity shrink-0"
                        title="Mejorar con IA"
                    >
                        <FiZap size={11} />
                    </button>

                    {hasSubtasks && (
                        <span className="flex items-center gap-0.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0 rounded-full shrink-0">
                            <FiList size={10} />{subtaskCount}
                        </span>
                    )}

                    <button
                        onClick={(e) => { e.stopPropagation(); setIsAddingChild(true); }}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity shrink-0"
                        title="Agregar subtarea"
                    >
                        <FiPlus size={12} />
                    </button>
                </div>

                {/* Assignee */}
                <div className="shrink-0 relative" onClick={(e) => e.stopPropagation()}>
                    <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => setAssigningSubtaskId(assigningSubtaskId === subtask.id ? null : subtask.id)}
                    >
                        {(() => {
                            const isCurrentUser = user && user.email === assignedEmail;
                            const displayName = isCurrentUser ? (user.displayName || (assignedUser ? assignedUser.displayName : assignedEmail)) : (assignedUser ? assignedUser.displayName : assignedEmail);
                            const photoURL = isCurrentUser ? (user.photoURL || (assignedUser ? assignedUser.photoURL : null)) : (assignedUser ? assignedUser.photoURL : null);

                            if (photoURL) {
                                return <img src={photoURL} alt="" className="w-6 h-6 rounded-full object-cover" title={displayName} />;
                            } else if (assignedEmail) {
                                return (
                                    <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold" title={displayName}>
                                        {displayName ? displayName.charAt(0).toUpperCase() : assignedEmail.charAt(0).toUpperCase()}
                                    </div>
                                );
                            } else {
                                return (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 hover:bg-gray-300" title="Asignar">
                                        <FiUser size={12} />
                                    </div>
                                );
                            }
                        })()}
                    </div>

                    {assigningSubtaskId === subtask.id && (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto">
                            <div className="p-2 text-xs text-gray-500 font-semibold border-b border-gray-100">Asignar a...</div>
                            {users.map(u => {
                                const isCurrentUser = user && user.email === u.email;
                                const displayName = isCurrentUser ? (user.displayName || u.displayName) : u.displayName;
                                const photoURL = isCurrentUser ? (user.photoURL || u.photoURL) : u.photoURL;

                                return (
                                    <button
                                        key={u.id}
                                        onClick={() => handleAssign(u.email)}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs"
                                    >
                                        {photoURL ? (
                                            <img src={photoURL} alt="" className="w-5 h-5 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                                <FiUser size={10} />
                                            </div>
                                        )}
                                        <span className="truncate">{displayName || u.email}</span>
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => handleAssign(null)}
                                className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2 text-xs border-t border-gray-100"
                            >
                                <FiX size={12} /> Quitar asignación
                            </button>
                        </div>
                    )}
                </div>

                {/* Date */}
                <div className="shrink-0 relative" onClick={(e) => e.stopPropagation()}>
                    <div
                        className={`text-[11px] cursor-pointer hover:bg-gray-100 px-1.5 py-0.5 rounded ${dateStr ? 'text-gray-700' : 'text-gray-400'}`}
                        onClick={(e) => {
                            if (datePickerSubtaskId?.id === subtask.id) {
                                setDatePickerSubtaskId(null);
                            } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const popoverWidth = 620;
                                const popoverHeight = 380;
                                const left = Math.max(8, Math.min(rect.left, window.innerWidth - popoverWidth));
                                const topBelow = rect.bottom + 4;
                                const topAbove = rect.top - popoverHeight - 4;
                                const top = (topBelow + popoverHeight > window.innerHeight && topAbove > 0) ? topAbove : topBelow;
                                setDatePickerSubtaskId({
                                    id: subtask.id,
                                    position: { top, left }
                                });
                            }
                        }}
                    >
                        {dateStr ? (
                            <span className={new Date(dateStr) < new Date() ? 'text-red-500' : ''}>
                                {formatDateDisplay(dateStr)}
                            </span>
                        ) : (
                            <span className="flex items-center gap-1"><FiCalendar size={12} /> Fecha</span>
                        )}
                    </div>

                    {datePickerSubtaskId?.id === subtask.id && (
                        <DatePickerPopover
                            date={dateStr}
                            onDateChange={(newDate) => handleUpdate({ fechaVencimiento: newDate })}
                            onClose={() => setDatePickerSubtaskId(null)}
                            position={datePickerSubtaskId.position}
                        />
                    )}
                </div>

                {/* Priority/Actions */}
                <div className="shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <select
                        value={subtask.prioridad || 'media'}
                        onChange={(e) => handleUpdate({ prioridad: e.target.value })}
                        className="bg-transparent border-none text-[11px] text-gray-500 focus:ring-0 p-0 cursor-pointer"
                    >
                        <option value="baja">Baja</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                    </select>
                    <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity">
                        <FiTrash2 size={12} />
                    </button>
                </div>
            </div>

            {/* Render child subtasks recursively */}
            {isExpanded && hasSubtasks && (subtask.subtareas || []).map(child => (
                <SubtaskRow
                    key={child.id}
                    subtask={child}
                    task={task}
                    level={level + 1}
                    parentPath={currentPath}
                />
            ))}

            {/* Add child input */}
            {isAddingChild && (
                <div className="p-3 ml-6 bg-blue-50/50 rounded-lg border border-blue-100 space-y-2" style={{ marginLeft: `${(level + 1) * 24}px` }}>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={isRecordingChild ? `${newChildTitle}${newChildTitle ? ' ' : ''}${interimChild}` : newChildTitle}
                                onChange={(e) => setNewChildTitle(e.target.value)}
                                placeholder={isRecordingChild ? "Escuchando..." : "Nombre de la subtarea..."}
                                className={`w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 pr-16 ${isRecordingChild ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) handleAddChild();
                                    if (e.key === 'Escape') resetChildForm();
                                }}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!newChildTitle.trim()) return;
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setChildAddAiPopover({ initialContent: newChildTitle, position: { top: rect.bottom + 5, left: rect.left } });
                                    }}
                                    className={`hover:scale-110 transition-transform ${newChildTitle.trim() ? 'text-indigo-400 hover:text-indigo-600' : 'text-gray-300 cursor-not-allowed'}`}
                                    title="Mejorar con IA"
                                >
                                    <FiZap size={14} />
                                </button>
                                <button
                                    onClick={startRecordingChild}
                                    className={`hover:scale-110 transition-transform ${isRecordingChild ? 'text-red-500 animate-pulse' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <FiMic size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowChildDatePicker(!showChildDatePicker)}
                                className={`flex items-center gap-1.5 bg-white border rounded-lg px-2.5 py-1.5 text-xs transition-colors ${newChildDate ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                            >
                                <FiCalendar size={13} />
                                {newChildDate ? format(new Date(newChildDate), "d MMM, HH:mm", { locale: es }) : 'Fecha'}
                                {newChildDate && (
                                    <FiX size={12} className="ml-1 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setNewChildDate(''); }} />
                                )}
                            </button>
                            {showChildDatePicker && (
                                <DatePickerPopover
                                    date={newChildDate || null}
                                    onDateChange={(val) => { setNewChildDate(val || ''); setShowChildDatePicker(false); }}
                                    onClose={() => setShowChildDatePicker(false)}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <FiUser size={13} className="text-gray-400" />
                            <HxSelect
                                size="sm"
                                minWidth={150}
                                popWidth={280}
                                value={newChildAssignee}
                                onChange={setNewChildAssignee}
                                options={[
                                    { value: '', label: 'Sin asignar', icon: LUser },
                                    ...(users || []).slice().sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => ({
                                    value: u.email, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#0a5cff',
                                })),
                                ]}
                                placeholder="Sin asignar"
                                searchPlaceholder="Buscar responsable…"
                                ariaLabel="Responsable"
                            />
                        </div>
                        <div className="flex-1" />
                        <button onClick={handleAddChild} className="text-xs bg-blue-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-blue-700 transition-colors">Agregar</button>
                        <button onClick={resetChildForm} className="text-xs text-gray-500 px-2 hover:text-gray-700">Cancelar</button>
                    </div>
                    {childAddAiPopover && (
                        <AIPopover
                            initialContent={childAddAiPopover.initialContent}
                            position={childAddAiPopover.position}
                            targetName="subtarea"
                            onClose={() => setChildAddAiPopover(null)}
                            onInsert={(content) => { setNewChildTitle(content); setChildAddAiPopover(null); }}
                        />
                    )}
                </div>
            )}
            {/* AI Popover */}
            {aiPopover && (
                <AIPopover
                    initialContent={aiPopover.initialContent}
                    position={aiPopover.position}
                    targetName="subtarea"
                    onClose={() => setAiPopover(null)}
                    onInsert={async (content) => {
                        await handleUpdate({ titulo: content });
                        setAiPopover(null);
                    }}
                />
            )}

            <ListeningOverlay
                isRecording={isRecordingChild}
                transcript={interimChild}
                onStop={stopRecordingChild}
            />

            <DeleteConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="¿Eliminar subtarea?"
                message={`Se eliminará "${subtask.titulo}" y todas sus sub-subtareas. Esta acción no se puede deshacer.`}
                itemType="subtarea"
            />
        </>
    );
};

const SubtaskTable = ({ rootTask, activeItem, currentPath = [] }) => {
    const { user } = useAuth();
    const { selectedProjectId, selectedFolderId, selectedListId, users } = useTask();

    // Use the task's own location IDs for Firestore operations
    const taskProjectId = rootTask?.projectId || selectedProjectId;
    const taskFolderId = rootTask?.folderId || selectedFolderId;
    const taskListId = rootTask?.listId || selectedListId;
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [newSubtaskDate, setNewSubtaskDate] = useState('');
    const [newSubtaskAssignee, setNewSubtaskAssignee] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [showRootDatePicker, setShowRootDatePicker] = useState(false);
    const [rootAddAiPopover, setRootAddAiPopover] = useState(null);

    // Voice Dictation for Root Subtasks
    const { isRecording, interimTranscript, startRecording, stopRecording } = useVoiceInput((text) => {
        setNewSubtaskTitle(prev => prev ? `${prev} ${text}` : text);
    });

    const handleAdd = async () => {
        if (!newSubtaskTitle.trim()) return;
        try {
            const subtaskData = {
                titulo: newSubtaskTitle,
                estado: 'planificacion'
            };
            if (newSubtaskDate) subtaskData.fechaVencimiento = newSubtaskDate;
            if (newSubtaskAssignee) subtaskData.asignados = [newSubtaskAssignee];
            await addSubtask(user.uid, taskProjectId, taskFolderId, taskListId, rootTask.id, subtaskData, currentPath);
            setNewSubtaskTitle('');
            setNewSubtaskDate('');
            setNewSubtaskAssignee('');
            setIsAdding(false);
            toast.success('Subtarea creada');
        } catch (error) {
            console.error(error);
            toast.error('Error al crear subtarea');
        }
    };

    const resetAddForm = () => {
        setIsAdding(false);
        setNewSubtaskTitle('');
        setNewSubtaskDate('');
        setNewSubtaskAssignee('');
        setShowRootDatePicker(false);
        setRootAddAiPopover(null);
    };

    return (
        <div className="w-full">
            {/* Subtask Rows */}
            <div className="space-y-0.5">
                {(activeItem?.subtareas || []).map(subtask => (
                    <SubtaskRow
                        key={subtask.id}
                        subtask={subtask}
                        task={rootTask}
                        level={0}
                        parentPath={currentPath}
                    />
                ))}
            </div>

            {/* Add New Subtask */}
            <div className="mt-2">
                {isAdding ? (
                    <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 space-y-2">
                        <div className="flex items-center gap-2">
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    value={isRecording ? `${newSubtaskTitle}${newSubtaskTitle ? ' ' : ''}${interimTranscript}` : newSubtaskTitle}
                                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                    placeholder={isRecording ? "Escuchando..." : "Nombre de la subtarea..."}
                                    className={`w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 pr-16 ${isRecording ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) handleAdd();
                                        if (e.key === 'Escape') resetAddForm();
                                    }}
                                />
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!newSubtaskTitle.trim()) return;
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setRootAddAiPopover({ initialContent: newSubtaskTitle, position: { top: rect.bottom + 5, left: rect.left } });
                                        }}
                                        className={`hover:scale-110 transition-transform ${newSubtaskTitle.trim() ? 'text-indigo-400 hover:text-indigo-600' : 'text-gray-300 cursor-not-allowed'}`}
                                        title="Mejorar con IA"
                                    >
                                        <FiZap size={14} />
                                    </button>
                                    <button
                                        onClick={startRecording}
                                        className={`hover:scale-110 transition-transform ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        <FiMic size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setShowRootDatePicker(!showRootDatePicker)}
                                    className={`flex items-center gap-1.5 bg-white border rounded-lg px-2.5 py-1.5 text-xs transition-colors ${newSubtaskDate ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                                >
                                    <FiCalendar size={13} />
                                    {newSubtaskDate ? format(new Date(newSubtaskDate), "d MMM, HH:mm", { locale: es }) : 'Fecha'}
                                    {newSubtaskDate && (
                                        <FiX size={12} className="ml-1 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setNewSubtaskDate(''); }} />
                                    )}
                                </button>
                                {showRootDatePicker && (
                                    <DatePickerPopover
                                        date={newSubtaskDate || null}
                                        onDateChange={(val) => { setNewSubtaskDate(val || ''); setShowRootDatePicker(false); }}
                                        onClose={() => setShowRootDatePicker(false)}
                                    />
                                )}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <FiUser size={13} className="text-gray-400" />
                                <HxSelect
                                size="sm"
                                minWidth={150}
                                popWidth={280}
                                value={newSubtaskAssignee}
                                onChange={setNewSubtaskAssignee}
                                options={[
                                    { value: '', label: 'Sin asignar', icon: LUser },
                                    ...(users || []).slice().sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => ({
                                    value: u.email, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#0a5cff',
                                })),
                                ]}
                                placeholder="Sin asignar"
                                searchPlaceholder="Buscar responsable…"
                                ariaLabel="Responsable"
                            />
                            </div>
                            <div className="flex-1" />
                            <button onClick={handleAdd} className="text-xs bg-blue-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-blue-700 transition-colors">Agregar</button>
                            <button onClick={resetAddForm} className="text-xs text-gray-500 px-2 hover:text-gray-700">Cancelar</button>
                        </div>
                        {rootAddAiPopover && (
                            <AIPopover
                                initialContent={rootAddAiPopover.initialContent}
                                position={rootAddAiPopover.position}
                                targetName="subtarea"
                                onClose={() => setRootAddAiPopover(null)}
                                onInsert={(content) => { setNewSubtaskTitle(content); setRootAddAiPopover(null); }}
                            />
                        )}
                    </div>
                ) : (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="flex items-center gap-1.5 text-gray-400 hover:text-blue-600 text-[12px] px-1.5 py-1 rounded hover:bg-gray-50 transition-colors w-full"
                    >
                        <FiPlus size={13} /> Agregar subtarea
                    </button>
                )}
            </div>

            <ListeningOverlay
                isRecording={isRecording}
                transcript={interimTranscript}
                onStop={stopRecording}
            />
        </div>
    );
};

export default SubtaskTable;
